import { useMemo, useState } from "react";
import Pagination from "@/components/base/Pagination";
import { Skeleton } from "@/components/base/Loading";
import { usePagination } from "@/hooks/usePagination";
import { downloadCsv, type CsvColumn } from "@/lib/csv";
import BatchDeviceList from "@/pages/admin/audit/components/BatchDeviceList";
import type { AuditLog } from "@/pages/admin/audit/hooks/useAuditLogs";

type LevelFilter = "all" | "success" | "warning" | "error" | "info";
type PeriodFilter = "all" | "today" | "7d" | "30d" | "custom";
type QuickFilter = "all" | "products";
type ProductSub = "all" | "create" | "update" | "status" | "delete";
type RankPeriod = "7d" | "30d";

interface RankRow {
  rank: number;
  name: string;
  count: number;
  recent: number;
  prev: number;
  delta: number;
  rate: number | null;
}

const LEVEL_META: Record<string, { label: string; badge: string; dot: string; icon: string }> = {
  success: { label: "成功", badge: "bg-primary-100 text-primary-700", dot: "bg-primary-500", icon: "ri-checkbox-circle-line" },
  warning: { label: "告警", badge: "bg-accent-100 text-accent-700", dot: "bg-accent-500", icon: "ri-alert-line" },
  error: { label: "异常", badge: "bg-accent-100 text-accent-800", dot: "bg-accent-600", icon: "ri-error-warning-line" },
  info: { label: "提示", badge: "bg-secondary-100 text-secondary-900", dot: "bg-secondary-500", icon: "ri-information-line" },
};

const LEVEL_FILTERS: { key: LevelFilter; label: string }[] = [
  { key: "all", label: "全部级别" },
  { key: "success", label: "成功" },
  { key: "warning", label: "告警" },
  { key: "error", label: "异常" },
  { key: "info", label: "提示" },
];

const PERIOD_FILTERS: { key: PeriodFilter; label: string }[] = [
  { key: "all", label: "全部时间" },
  { key: "today", label: "今天" },
  { key: "7d", label: "近 7 天" },
  { key: "30d", label: "近 30 天" },
  { key: "custom", label: "自定义" },
];

const QUICK_FILTERS: { key: QuickFilter; label: string; icon: string }[] = [
  { key: "all", label: "全部日志", icon: "ri-file-list-3-line" },
  { key: "products", label: "软件产品操作", icon: "ri-apps-2-line" },
];

// 「软件产品操作」涉及新增 / 更新 / 上下架 / 删除产品等留痕，按关键词归集。
function isProductLog(log: AuditLog): boolean {
  return log.action.includes("软件产品");
}

// 把产品相关留痕进一步归类到子标签。
function productOpKind(log: AuditLog): ProductSub {
  const action = log.action;
  if (action.includes("上架") || action.includes("下架") || action.includes("草稿")) return "status";
  if (action.includes("新增")) return "create";
  if (action.includes("更新")) return "update";
  if (action.includes("删除")) return "delete";
  return "all";
}

const PRODUCT_SUB_FILTERS: { key: ProductSub; label: string; icon: string }[] = [
  { key: "all", label: "全部", icon: "ri-apps-2-line" },
  { key: "create", label: "新增", icon: "ri-add-circle-line" },
  { key: "update", label: "更新", icon: "ri-edit-line" },
  { key: "status", label: "上下架", icon: "ri-toggle-line" },
  { key: "delete", label: "删除", icon: "ri-delete-bin-line" },
];

const RANK_PERIODS: { key: RankPeriod; label: string; days: number; currentLabel: string; prevLabel: string }[] = [
  { key: "7d", label: "近 7 天", days: 7, currentLabel: "近 7 天", prevLabel: "上一周期（7–14 天前）" },
  { key: "30d", label: "近 30 天", days: 30, currentLabel: "近 30 天", prevLabel: "上一周期（30–60 天前）" },
];

const PAGE_SIZE = 15;

function meta(level: string) {
  return LEVEL_META[level] ?? LEVEL_META.info;
}

function actorKey(log: AuditLog): string {
  return log.actor_name?.trim() || "系统";
}

function formatDateTime(input: string): string {
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function withinPeriod(iso: string, period: PeriodFilter, start: string, end: string): boolean {
  if (period === "all") return true;
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return true;
  if (period === "custom") {
    if (start) {
      const from = new Date(`${start}T00:00:00`).getTime();
      if (!Number.isNaN(from) && time < from) return false;
    }
    if (end) {
      const to = new Date(`${end}T23:59:59`).getTime();
      if (!Number.isNaN(to) && time > to) return false;
    }
    return true;
  }
  if (period === "today") return new Date(iso).toDateString() === new Date().toDateString();
  const day = 86400000;
  if (period === "7d") return Date.now() - time <= 7 * day;
  return Date.now() - time <= 30 * day;
}

const CSV_COLUMNS: CsvColumn<AuditLog>[] = [
  { key: "created_at", label: "时间", value: (row) => formatDateTime(row.created_at) },
  { key: "level", label: "级别", value: (row) => meta(row.level).label },
  { key: "actor_name", label: "操作人", value: (row) => row.actor_name ?? "系统" },
  { key: "action", label: "操作", value: (row) => row.action },
  { key: "detail", label: "详情", value: (row) => row.detail ?? "" },
  { key: "user_id", label: "操作人 ID", value: (row) => row.user_id ?? "" },
  { key: "id", label: "日志编号", value: (row) => row.id },
];

interface AuditLogListProps {
  logs: AuditLog[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

export default function AuditLogList({ logs, loading, error, onRetry }: AuditLogListProps) {
  const [level, setLevel] = useState<LevelFilter>("all");
  const [quick, setQuick] = useState<QuickFilter>("all");
  const [productSub, setProductSub] = useState<ProductSub>("all");
  const [period, setPeriod] = useState<PeriodFilter>("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [actor, setActor] = useState<string>("all");
  const [keyword, setKeyword] = useState("");
  const [exportAll, setExportAll] = useState(false);
  const [exported, setExported] = useState(false);
  const [rankPeriod, setRankPeriod] = useState<RankPeriod>("7d");
  const [rankExported, setRankExported] = useState(false);
  const [expandedBatchLog, setExpandedBatchLog] = useState<number | null>(null);

  const actors = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((log) => set.add(actorKey(log)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "zh-CN"));
  }, [logs]);

  const quickFiltered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    return logs.filter((log) => {
      if (quick === "products" && !isProductLog(log)) return false;
      if (level !== "all" && log.level !== level) return false;
      if (actor !== "all" && actorKey(log) !== actor) return false;
      if (!withinPeriod(log.created_at, period, customStart, customEnd)) return false;
      if (!term) return true;
      return (
        log.action.toLowerCase().includes(term) ||
        (log.detail ?? "").toLowerCase().includes(term) ||
        (log.actor_name ?? "").toLowerCase().includes(term)
      );
    });
  }, [logs, level, period, actor, keyword, customStart, customEnd, quick]);

  const filtered = useMemo(() => {
    if (quick !== "products" || productSub === "all") return quickFiltered;
    return quickFiltered.filter((log) => productOpKind(log) === productSub);
  }, [quickFiltered, quick, productSub]);

  const rankDays = RANK_PERIODS.find((item) => item.key === rankPeriod)?.days ?? 7;
  const rankMeta = RANK_PERIODS.find((item) => item.key === rankPeriod) ?? RANK_PERIODS[0];

  const ranking = useMemo(() => {
    const now = Date.now();
    const day = 86400000;
    const span = rankDays;
    const map = new Map<string, { count: number; recent: number; prev: number }>();
    filtered.forEach((log) => {
      const key = actorKey(log);
      const item = map.get(key) ?? { count: 0, recent: 0, prev: 0 };
      item.count += 1;
      const time = new Date(log.created_at).getTime();
      if (!Number.isNaN(time)) {
        if (now - time <= span * day) item.recent += 1;
        else if (now - time <= 2 * span * day) item.prev += 1;
      }
      map.set(key, item);
    });
    return Array.from(map.entries())
      .map(([name, value]) => {
        const delta = value.recent - value.prev;
        const rate = value.prev === 0 ? null : Math.round((delta / value.prev) * 100);
        return {
          name,
          count: value.count,
          recent: value.recent,
          prev: value.prev,
          delta,
          rate,
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 6)
      .map((item, index) => ({ ...item, rank: index + 1 }));
  }, [filtered, rankDays]);

  const rankingMax = ranking.length > 0 ? ranking[0].count : 1;

  const rankTitle =
    quick === "products" ? "产品维护榜单 · 谁最常维护产品" : "操作量榜单 · 按操作人聚合";
  const rankExportName = quick === "products" ? "产品维护榜单" : "操作量榜单";

  const exportRows = exportAll ? logs : filtered;

  const { page, totalPages, total, pageSize, pagedItems, setPage } = usePagination(
    filtered,
    PAGE_SIZE,
    `${level}|${quick}|${productSub}|${period}|${actor}|${keyword}|${customStart}|${customEnd}`,
  );

  const countFor = (key: LevelFilter) => (key === "all" ? logs.length : logs.filter((log) => log.level === key).length);
  const quickCount = (key: QuickFilter) =>
    key === "all" ? logs.length : logs.filter((log) => isProductLog(log)).length;
  const productSubCount = (key: ProductSub) =>
    key === "all" ? quickFiltered.length : quickFiltered.filter((log) => productOpKind(log) === key).length;

  const handleExport = () => {
    if (exportRows.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`审计日志_${stamp}.csv`, CSV_COLUMNS, exportRows);
    setExported(true);
    window.setTimeout(() => setExported(false), 2000);
  };

  const handleRankExport = () => {
    if (ranking.length === 0) return;
    const columns: CsvColumn<RankRow>[] = [
      { key: "rank", label: "排名", value: (row) => row.rank },
      { key: "name", label: "操作人", value: (row) => row.name },
      { key: "count", label: "总操作次数", value: (row) => row.count },
      { key: "recent", label: `${rankMeta.currentLabel}次数`, value: (row) => row.recent },
      { key: "prev", label: `上一周期次数（${rankMeta.prevLabel}）`, value: (row) => row.prev },
      { key: "delta", label: "绝对增减", value: (row) => (row.delta > 0 ? `+${row.delta}` : `${row.delta}`) },
      {
        key: "rate",
        label: "环比变化率",
        value: (row) => (row.rate === null ? "新增" : `${row.rate > 0 ? "+" : ""}${row.rate}%`),
      },
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    const periodLabel = rankMeta.label.replace(/\s+/g, "");
    downloadCsv(`${rankExportName}_${periodLabel}_${stamp}.csv`, columns, ranking);
    setRankExported(true);
    window.setTimeout(() => setRankExported(false), 2000);
  };

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-sm">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line" />
          </span>
          <input
            type="text"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索操作、详情或操作人"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-9 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          {keyword && (
            <button
              type="button"
              onClick={() => setKeyword("")}
              aria-label="清除搜索"
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-secondary-700">
              <i className="ri-user-line" />
            </span>
            <select
              value={actor}
              onChange={(event) => setActor(event.target.value)}
              aria-label="按操作人筛选"
              className="cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-8 pr-8 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
            >
              <option value="all">全部操作人</option>
              {actors.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-arrow-down-s-line" />
            </span>
          </div>
          <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
            {PERIOD_FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setPeriod(item.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                  period === item.key
                    ? "bg-background-50 font-medium text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-secondary-700">
                <i className="ri-calendar-line" />
              </span>
              <input
                type="date"
                value={customStart}
                onChange={(event) => setCustomStart(event.target.value)}
                aria-label="起始日期"
                className="cursor-pointer rounded border border-background-300 bg-background-50 px-2 py-1 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
              />
              <span className="text-xs text-foreground-400">至</span>
              <input
                type="date"
                value={customEnd}
                onChange={(event) => setCustomEnd(event.target.value)}
                aria-label="结束日期"
                className="cursor-pointer rounded border border-background-300 bg-background-50 px-2 py-1 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
              />
              {(customStart || customEnd) && (
                <button
                  type="button"
                  onClick={() => {
                    setCustomStart("");
                    setCustomEnd("");
                  }}
                  className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
                  aria-label="清除日期范围"
                >
                  <i className="ri-close-line text-xs" />
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={exportAll}
            onClick={() => setExportAll((value) => !value)}
            title="开启后导出全部日志，不受当前筛选影响"
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span
              className={`relative h-4 w-7 rounded-full transition-colors ${
                exportAll ? "bg-primary-500" : "bg-background-300"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-background-50 transition-transform ${
                  exportAll ? "translate-x-3" : "translate-x-0"
                }`}
              />
            </span>
            导出全部
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exportRows.length === 0}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
              <i className={exported ? "ri-check-line" : "ri-download-2-line"} />
            </span>
            {exported ? "已导出" : exportAll ? "导出全部日志" : "导出当前筛选"}
          </button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 text-xs text-foreground-500">
          <span className="flex h-4 w-4 items-center justify-center text-accent-600">
            <i className="ri-flashlight-line" />
          </span>
          快捷筛选
        </span>
        {QUICK_FILTERS.map((item) => {
          const active = quick === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setQuick(item.key)}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                active
                  ? "bg-accent-500 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={item.icon} />
              </span>
              {item.label}
              <span className={active ? "text-background-200" : "text-foreground-400"}>{quickCount(item.key)}</span>
            </button>
          );
        })}
      </div>

      {quick === "products" && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2">
          <span className="flex items-center gap-1.5 text-xs text-foreground-500">
            <span className="flex h-4 w-4 items-center justify-center text-secondary-700">
              <i className="ri-filter-3-line" />
            </span>
            产品操作类型
          </span>
          {PRODUCT_SUB_FILTERS.map((item) => {
            const active = productSub === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setProductSub(item.key)}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                  active
                    ? "bg-secondary-500 font-medium text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                }`}
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={item.icon} />
                </span>
                {item.label}
                <span className={active ? "text-background-200" : "text-foreground-400"}>
                  {productSubCount(item.key)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {LEVEL_FILTERS.map((item) => {
          const active = level === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setLevel(item.key)}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                active
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
              }`}
            >
              {item.label}
              <span className={active ? "text-background-200" : "text-foreground-400"}>{countFor(item.key)}</span>
            </button>
          );
        })}
      </div>

      <p className="mb-4 text-xs text-foreground-500">
        当前筛选命中 <strong className="text-foreground-800">{filtered.length}</strong> 条，共 {logs.length} 条日志
        {exportAll && <span className="ml-2 text-primary-700">导出将包含全部 {logs.length} 条，忽略筛选</span>}
      </p>

      {!loading && !error && ranking.length > 0 && (
        <div className="mb-5 rounded-lg border border-background-200 bg-background-50 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
              <span className="flex h-4 w-4 items-center justify-center text-accent-700">
                <i className="ri-bar-chart-2-line" />
              </span>
              {rankTitle}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
                {RANK_PERIODS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setRankPeriod(item.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[11px] transition-colors ${
                      rankPeriod === item.key
                        ? "bg-background-50 font-medium text-foreground-950"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                disabled={ranking.length === 0}
                onClick={handleRankExport}
                title={`导出榜单 CSV（含${rankMeta.currentLabel}与上一周期的次数与环比变化率）`}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
                  <i className={rankExported ? "ri-check-line" : "ri-download-2-line"} />
                </span>
                {rankExported ? "已导出" : "导出榜单"}
              </button>
            </div>
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500">
            <span>基于当前筛选命中 {filtered.length} 条</span>
            <span className="inline-flex items-center gap-1 text-primary-700">
              <i className="ri-arrow-up-line" />
              {rankMeta.currentLabel}上升
            </span>
            <span className="inline-flex items-center gap-1 text-accent-700">
              <i className="ri-arrow-down-line" />
              {rankMeta.currentLabel}下降
            </span>
            <span className="hidden sm:inline">对比{rankMeta.prevLabel} · 展示环比变化率</span>
            <span className="inline-flex items-center gap-1 text-secondary-700">
              <i className="ri-cursor-line" />
              点操作人可筛出其操作留痕
            </span>
          </div>
          <ul className="flex flex-col gap-2.5">
            {ranking.map((item, index) => (
              <li key={item.name} className="flex items-center gap-3">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-medium ${
                    index === 0 ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-600"
                  }`}
                >
                  {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => setActor(item.name)}
                  title={`点击筛出「${item.name}」的全部操作留痕`}
                  className={`flex w-20 shrink-0 cursor-pointer items-center gap-1 truncate rounded-md px-1.5 py-0.5 text-xs transition-colors sm:w-28 ${
                    actor === item.name
                      ? "bg-secondary-500 font-medium text-background-50"
                      : "text-foreground-900 hover:bg-secondary-100 hover:text-secondary-900"
                  }`}
                >
                  <span className="truncate">{item.name}</span>
                </button>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-background-100">
                  <div
                    className="h-full rounded-full bg-primary-500"
                    style={{ width: `${Math.max(4, Math.round((item.count / rankingMax) * 100))}%` }}
                  />
                </div>
                <span className="w-12 shrink-0 text-right text-xs font-medium text-foreground-950">{item.count}</span>
                {item.delta === 0 ? (
                  <span
                    title={`${rankMeta.currentLabel} ${item.recent} 次 vs 上一周期 ${item.prev} 次 · 环比持平`}
                    className="w-20 shrink-0 text-right text-[10px] text-foreground-400"
                  >
                    持平
                  </span>
                ) : (
                  <span
                    title={`${rankMeta.currentLabel} ${item.recent} 次 vs 上一周期 ${item.prev} 次`}
                    className={`flex w-20 shrink-0 flex-col items-end leading-tight ${
                      item.delta > 0 ? "text-primary-700" : "text-accent-700"
                    }`}
                  >
                    <span className="flex items-center gap-0.5 text-[10px] font-medium">
                      <i className={item.delta > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
                      {item.rate === null ? "新增" : `${item.rate > 0 ? "+" : ""}${item.rate}%`}
                    </span>
                    <span className="text-[9px] text-foreground-400">
                      {item.delta > 0 ? "+" : "-"}
                      {Math.abs(item.delta)} 次
                    </span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2, 3, 4].map((item) => (
            <div key={item} className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
              <Skeleton className="h-8 w-8 rounded-md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3.5 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-error-warning-line text-xl" />
          </span>
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            重新加载
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-file-history-line text-2xl" />
          </span>
          <p className="text-sm font-medium text-foreground-800">没有匹配的操作日志</p>
          <p className="max-w-sm text-xs text-foreground-500">调整筛选条件或搜索关键词再试试。</p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-background-200">
            <div className="hidden bg-background-100 px-5 py-3 text-xs font-medium text-foreground-600 lg:grid lg:grid-cols-12 lg:gap-3">
              <span className="col-span-3">时间</span>
              <span className="col-span-2">级别</span>
              <span className="col-span-2">操作人</span>
              <span className="col-span-5">操作 / 详情</span>
            </div>
            {pagedItems.map((log) => {
              const levelStyle = meta(log.level);
              const isBatch = log.action === "批量解绑非活跃设备" && Boolean(log.batch_id);
              const isExpanded = isBatch && expandedBatchLog === log.id;
              return (
                <div key={log.id} className="border-t border-background-200 bg-background-50">
                  <div className="grid gap-2 px-5 py-4 lg:grid-cols-12 lg:items-center lg:gap-3">
                    <div className="flex items-center gap-2 lg:col-span-3">
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${levelStyle.badge}`}>
                        <i className={`${levelStyle.icon} text-sm`} />
                      </span>
                      <span className="text-xs text-foreground-700">{formatDateTime(log.created_at)}</span>
                    </div>
                    <div className="lg:col-span-2">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${levelStyle.badge}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${levelStyle.dot}`} />
                        {levelStyle.label}
                      </span>
                    </div>
                    <div className="lg:col-span-2">
                      <p className="truncate text-sm text-foreground-900">{actorKey(log)}</p>
                      <p className="text-[10px] text-foreground-400">#{log.id}</p>
                    </div>
                    <div className="lg:col-span-5">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground-950">
                        {log.action}
                        {isBatch && (
                          <button
                            type="button"
                            onClick={() => setExpandedBatchLog(isExpanded ? null : log.id)}
                            title="展开查看本次清理回收的设备"
                            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800 transition-colors hover:bg-accent-200"
                          >
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className={isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                            </span>
                            {isExpanded ? "收起设备" : "查看回收设备"}
                          </button>
                        )}
                      </p>
                      {log.detail && <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">{log.detail}</p>}
                    </div>
                  </div>
                  {isExpanded && log.batch_id && (
                    <div className="px-5 pb-4">
                      <BatchDeviceList batchId={log.batch_id} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onChange={setPage}
            itemLabel="条日志"
          />
        </>
      )}
    </div>
  );
}